/**
 * RemnantCoverPDF.js — Cover PDF for Lulu casewrap hardcover printing.
 *
 * Supports 4 cover styles:
 *   1. 'photo'   — Full-bleed photo, title in white over dark gradient
 *   2. 'framed'  — Charcoal background, photo in elegant cream frame, title above
 *   3. 'classic' — Text-only, charcoal with cream typography (no photo needed)
 *   4. 'minimal' — Large title, small accent photo, charcoal with negative space
 *
 * Lulu 8.5"×11" Casewrap Hardcover cover specs:
 *   Spine width: LOOKUP TABLE (not formula)
 *   See: https://help.api.lulu.com/en/support/solutions/articles/64000254616
 *
 *   Layout (left to right):
 *     [0.125" bleed] [0.75" back wrap] [8.5" back cover] [spine] [8.5" front cover] [0.75" front wrap] [0.125" bleed]
 *   Height:
 *     [0.125" bleed] [0.75" wrap] [11" trim] [0.75" wrap] [0.125" bleed]
 *
 *   Casewrap hinge: 0.25" groove from spine on both covers (per Lulu docs).
 *   Safe zone: 0.5" from trim edge (per Lulu docs).
 *   Content is shifted 0.125" toward fore-edge (half the hinge width) so content
 *   centers on the optical midpoint of the visible flat board, not the full trim.
 *
 * Usage:
 *   <RemnantCoverPDF title="Tales From My Life" subtitle="Stories worth keeping" author="By Grandma" pageCount={36} coverStyle="photo" coverPhotoUrl="data:image/jpeg;base64,..." />
 */

import React from 'react';
import { BOTANICAL_FRAMED, BOTANICAL_CLASSIC, CHARCOAL_FRAMED, CHARCOAL_CLASSIC, FLORAL_WRAP, FLORAL_WRAP_TITLEONLY, getFramedPhotoSlot, floralTitleFontPt, floralFlourishWidthPt } from './constants/coverLayout';
import { COVER, calculateSpineWidth, getCoverDimensions, mustardPlumOpeningFraction, fullWrapPlacement } from './constants/coverGeometry';
import MustardPlumFlourish from './assets/mustard_plum_flourish.png';
import {
  Document,
  Page,
  Text,
  View,
  Image,
  StyleSheet,
  Font,
} from '@react-pdf/renderer';

import LoraRegular from './fonts/Lora-Regular.ttf';
import LoraBold from './fonts/Lora-Bold.ttf';
import LoraItalic from './fonts/Lora-Italic.ttf';
import LoraBoldItalic from './fonts/Lora-BoldItalic.ttf';

Font.register({
  family: 'Lora',
  fonts: [
    { src: LoraRegular, fontWeight: 400 },
    { src: LoraBold, fontWeight: 700 },
    { src: LoraItalic, fontWeight: 400, fontStyle: 'italic' },
    { src: LoraBoldItalic, fontWeight: 700, fontStyle: 'italic' },
  ],
});

Font.registerHyphenationCallback(word => [word]);

// ═══════════════════════════════════════════════════════════════
// SPINE CALCULATION + COVER DIMENSIONS
// ═══════════════════════════════════════════════════════════════
//
// COVER-002: this math now lives in a single place —
// src/constants/coverGeometry.js — and is imported here (and by
// coverLayout.js) rather than redefined. `calculateSpineWidth` and
// `getCoverDimensions` are re-exported for backward compatibility (App.js
// imports `getCoverDimensions` from this module).
export { calculateSpineWidth, getCoverDimensions };

// ═══════════════════════════════════════════════════════════════
// DEBUG GUIDE LINES — set debug={true} on RemnantCoverPDF to show
// ═══════════════════════════════════════════════════════════════

const DebugGuides = ({ dim }) => {
  const topEdge = (COVER.bleed + COVER.wrap) * 72;
  const bottomEdge = topEdge + COVER.trimH * 72;
  const frontCenter = dim.frontStartPt + dim.panelW / 2;
  return (
    <>
      {/* Front cover LEFT edge — solid red */}
      <View style={{ position: 'absolute', left: dim.frontStartPt, top: 0, width: 1, height: dim.heightPt, backgroundColor: 'red' }} />
      {/* Front cover RIGHT edge — solid red */}
      <View style={{ position: 'absolute', left: dim.frontEndPt, top: 0, width: 1, height: dim.heightPt, backgroundColor: 'red' }} />
      {/* Front cover CENTER — solid blue */}
      <View style={{ position: 'absolute', left: frontCenter, top: 0, width: 1, height: dim.heightPt, backgroundColor: 'blue' }} />
      {/* Spine LEFT edge — green */}
      <View style={{ position: 'absolute', left: dim.spineStartPt, top: 0, width: 1, height: dim.heightPt, backgroundColor: 'green' }} />
      {/* Spine RIGHT edge — green */}
      <View style={{ position: 'absolute', left: dim.spineStartPt + dim.spinePt, top: 0, width: 1, height: dim.heightPt, backgroundColor: 'green' }} />
      {/* Top trim edge — red horizontal */}
      <View style={{ position: 'absolute', left: 0, top: topEdge, width: dim.widthPt, height: 1, backgroundColor: 'red', opacity: 0.5 }} />
      {/* Bottom trim edge — red horizontal */}
      <View style={{ position: 'absolute', left: 0, top: bottomEdge, width: dim.widthPt, height: 1, backgroundColor: 'red', opacity: 0.5 }} />
      {/* Vertical center of front panel — dashed via thin purple */}
      <View style={{ position: 'absolute', left: frontCenter - 20, top: topEdge + 20, width: 40, backgroundColor: '#fff', padding: 4, borderRadius: 2 }}>
        <Text style={{ fontFamily: 'Lora', fontSize: 6, color: 'blue', textAlign: 'center' }}>CENTER</Text>
        <Text style={{ fontFamily: 'Lora', fontSize: 5, color: 'blue', textAlign: 'center' }}>{Math.round(frontCenter)}pt</Text>
      </View>
      {/* Label: front left edge */}
      <View style={{ position: 'absolute', left: dim.frontStartPt + 2, top: topEdge + 20, backgroundColor: '#fff', padding: 3, borderRadius: 2 }}>
        <Text style={{ fontFamily: 'Lora', fontSize: 5, color: 'red' }}>FRONT LEFT {Math.round(dim.frontStartPt)}pt</Text>
      </View>
      {/* Label: front right edge */}
      <View style={{ position: 'absolute', left: dim.frontEndPt - 80, top: topEdge + 20, backgroundColor: '#fff', padding: 3, borderRadius: 2 }}>
        <Text style={{ fontFamily: 'Lora', fontSize: 5, color: 'red' }}>FRONT RIGHT {Math.round(dim.frontEndPt)}pt</Text>
      </View>
      {/* Label: spine */}
      <View style={{ position: 'absolute', left: dim.spineStartPt + 2, top: topEdge + 40, backgroundColor: '#fff', padding: 3, borderRadius: 2 }}>
        <Text style={{ fontFamily: 'Lora', fontSize: 5, color: 'green' }}>SPINE {dim.spinePt.toFixed(1)}pt</Text>
      </View>
      {/* Safe zone box — 0.5" inset on front cover, shifted for hinge */}
      <View style={{ position: 'absolute', left: dim.frontStartPt + dim.safeInset + dim.hingeCompensation, top: topEdge + dim.safeInset, width: dim.panelW - dim.safeInset * 2, height: dim.panelH - dim.safeInset * 2, borderWidth: 0.5, borderColor: 'orange', borderStyle: 'dashed' }} />
      <View style={{ position: 'absolute', left: dim.frontStartPt + dim.safeInset + dim.hingeCompensation + 2, top: topEdge + dim.safeInset + 2, backgroundColor: '#fff', padding: 2, borderRadius: 2 }}>
        <Text style={{ fontFamily: 'Lora', fontSize: 5, color: 'orange' }}>SAFE ZONE (0.5" + hinge offset)</Text>
      </View>
    </>
  );
};

// ═══════════════════════════════════════════════════════════════
// BACK COVER + SPINE (shared by all styles)
// ═══════════════════════════════════════════════════════════════

const BackAndSpine = ({ title, dim, coverDesign = 'charcoal', botanicalImageUrl = null }) => {
  const isBotanical = coverDesign !== 'charcoal';
  return (
  <>
    {/* Back cover */}
    <View style={{
      position: 'absolute',
      left: dim.backStartPt,
      top: (COVER.bleed + COVER.wrap) * 72,
      width: COVER.trimW * 72,
      height: COVER.trimH * 72,
      justifyContent: 'center',
      alignItems: 'center',
      paddingLeft: dim.safeInset - dim.hingeCompensation,
      paddingRight: dim.safeInset + dim.hingeCompensation,
      paddingTop: dim.safeInset,
      paddingBottom: dim.safeInset,
    }}>
      {isBotanical ? (
        <>
          <Image src={botanicalImageUrl} style={{
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center top',
          }} />
          {/* Blank back — wrap art carries the whole panel */}
        </>
      ) : (
        <>
          <Text style={{
            fontFamily: 'Lora', fontStyle: 'italic', fontSize: 15,
            color: '#e0d4be', textAlign: 'center', lineHeight: 1.6,
          }}>Stories bound to last.</Text>
          <Text style={{
            fontFamily: 'Lora', fontSize: 13, color: '#c9a84c',
            letterSpacing: 4, marginTop: 32,
          }}>Talebound</Text>
        </>
      )}
    </View>

    {/* Spine */}
    <View style={{
      position: 'absolute',
      left: dim.spineStartPt,
      top: (COVER.bleed + COVER.wrap) * 72,
      width: dim.spinePt,
      height: COVER.trimH * 72,
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      {/* Empty spine — no text */}
    </View>
  </>
  );
};

// ═══════════════════════════════════════════════════════════════
// FRONT COVER — CLASSIC (text-only, no photo)
// ═══════════════════════════════════════════════════════════════

/** Classic on charcoal — centered text with rules */
const FrontClassicCharcoal = ({ title, subtitle, author, dim }) => (
  <View style={{
    position: 'absolute',
    left: dim.frontStartPt,
    top: (COVER.bleed + COVER.wrap) * 72,
    width: COVER.trimW * 72,
    height: COVER.trimH * 72,
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: dim.safeInset + dim.hingeCompensation,
    paddingRight: dim.safeInset - dim.hingeCompensation,
    paddingTop: dim.safeInset,
    paddingBottom: dim.safeInset,
  }}>
    <Text style={{
      fontFamily: 'Lora', fontSize: 13, color: '#c9a84c',
      letterSpacing: 4, marginBottom: 18,
    }}>Talebound</Text>
    <View style={{ width: 60, height: 0.75, backgroundColor: '#6b5e4e', marginBottom: 14 }} />
    <Text style={{
      fontFamily: 'Lora', fontSize: 30, color: '#f5ece0',
      textAlign: 'center', marginBottom: 10, fontWeight: 500,
    }}>{title}</Text>
    <View style={{ width: 60, height: 0.75, backgroundColor: '#6b5e4e', marginBottom: 14 }} />
    {subtitle ? <Text style={{
      fontFamily: 'Lora', fontStyle: 'italic', fontSize: 14,
      color: '#f0e6d4', textAlign: 'center', lineHeight: 1.5,
    }}>{subtitle}</Text> : null}
    {author ? <Text style={{
      fontFamily: 'Lora', fontStyle: 'italic', fontSize: 13,
      color: '#e0d4be', marginTop: 18,
    }}>{author}</Text> : null}
  </View>
);

/** Classic on botanical — full-bleed image with top-left frosted panel (matches landing page) */
const FrontClassicBotanical = ({ title, subtitle, author, botanicalImageUrl, dim }) => {
  const panelLeft = dim.safeInset + dim.hingeCompensation + 10;
  const panelTop = dim.safeInset + 10;

  return (
  <View style={{
    position: 'absolute',
    left: dim.frontFullLeft,
    top: dim.frontFullTop,
    width: dim.panelW,
    height: dim.panelH,
  }}>
    {botanicalImageUrl && (
      <Image src={botanicalImageUrl} style={{
        position: 'absolute', top: 0, left: 0,
        width: '100%', height: '100%',
        objectFit: 'cover', objectPosition: 'center top',
      }} />
    )}

    {/* Frosted panel — top-left, double border */}
    <View style={{
      position: 'absolute',
      left: panelLeft,
      top: panelTop,
      width: 155,
      backgroundColor: 'rgba(250,245,237,0.92)',
      borderWidth: 0.75,
      borderColor: 'rgba(190,155,120,0.38)',
      padding: 14,
    }}>
      <Text style={{
        fontFamily: 'Lora', fontSize: 22, fontWeight: 500,
        color: '#1e0e06', lineHeight: 1.2, marginBottom: 10,
      }}>{title}</Text>
      <View style={{ width: 28, height: 0.5, backgroundColor: 'rgba(160,110,75,0.45)', marginBottom: 9 }} />
      {subtitle ? <Text style={{
        fontFamily: 'Lora', fontStyle: 'italic', fontSize: 10,
        color: '#5a3820', lineHeight: 1.5, marginBottom: 4,
      }}>{subtitle}</Text> : null}
      {author ? <Text style={{
        fontFamily: 'Lora', fontStyle: 'italic', fontSize: 9.5,
        color: '#6a4028', lineHeight: 1.6, marginBottom: 2,
      }}>{author}</Text> : null}
    </View>

    {/* Outer outline border */}
    <View style={{
      position: 'absolute',
      left: panelLeft - 4,
      top: panelTop - 4,
      width: 155 + 8,
      borderWidth: 0.5,
      borderColor: 'rgba(240,230,215,0.6)',
      height: 'auto',
    }} />

    {/* Talebound wordmark — bottom center */}
    <Text style={{
      position: 'absolute', bottom: dim.safeInset,
      left: 0, right: 0, textAlign: 'center',
      fontFamily: 'Lora', fontSize: 7.5,
      letterSpacing: 5, color: 'rgba(90,52,28,0.38)',
    }}>TALEBOUND</Text>
  </View>
  );
};

// ═══════════════════════════════════════════════════════════════
// FRONT COVER — FRAMED (with cover photo)
// ═══════════════════════════════════════════════════════════════

/** Framed on charcoal — centered text with framed photo */
const FrontFramedCharcoal = ({ title, subtitle, author, coverPhotoUrl, coverPhotoY = 50, coverPhotoOrientation = 'landscape', dim }) => {
  const photoSlot = getFramedPhotoSlot(coverPhotoOrientation);
  return (
  // paddingLeft > paddingRight shifts the content block toward the fore-edge by
  // dim.hingeCompensation (0.125" = optical center of the visible flat board),
  // centering it on the hinge rather than the bare geometric trim midpoint.
  <View style={{
    position: 'absolute',
    left: dim.frontStartPt,
    top: (COVER.bleed + COVER.wrap) * 72,
    width: COVER.trimW * 72,
    height: COVER.trimH * 72,
    justifyContent: 'center',
    alignItems: 'center',
    paddingLeft: dim.safeInset + dim.hingeCompensation,
    paddingRight: dim.safeInset - dim.hingeCompensation,
    paddingTop: dim.safeInset,
    paddingBottom: dim.safeInset,
  }}>
    <Text style={{
      fontFamily: 'Lora', fontSize: 13, color: '#c9a84c',
      letterSpacing: 4, marginBottom: 14,
    }}>Talebound</Text>
    <View style={{ width: 50, height: 0.75, backgroundColor: '#6b5e4e', marginBottom: 16 }} />
    <Text style={{
      fontFamily: 'Lora', fontSize: 26, color: '#f5ece0',
      textAlign: 'center', marginBottom: 16,
    }}>{title}</Text>
    <View style={{ width: 50, height: 0.75, backgroundColor: '#6b5e4e', marginBottom: 20 }} />
    {coverPhotoUrl && (
      <View style={{
        width: photoSlot.widthPt, height: photoSlot.heightPt,
        border: `${CHARCOAL_FRAMED.photoBorderWidth}pt solid ${CHARCOAL_FRAMED.photoBorderColor}`,
        padding: CHARCOAL_FRAMED.photoPadding, marginBottom: 20,
      }}>
        <Image src={coverPhotoUrl} style={{
          width: '100%', height: '100%', objectFit: 'cover',
          objectPosition: `center ${coverPhotoY}%`,
        }} />
      </View>
    )}
    {subtitle ? <Text style={{
      fontFamily: 'Lora', fontStyle: 'italic', fontSize: 14,
      color: '#f0e6d4', textAlign: 'center', lineHeight: 1.5,
    }}>{subtitle}</Text> : null}
    {author ? <Text style={{
      fontFamily: 'Lora', fontStyle: 'italic', fontSize: 13,
      color: '#e0d4be', marginTop: 14,
    }}>{author}</Text> : null}
  </View>
  );
};

/** Framed on botanical — full-bleed image with top-left frosted panel + photo frame */
const FrontFramedBotanical = ({ title, subtitle, author, coverPhotoUrl, coverPhotoY = 50, coverPhotoOrientation = 'landscape', botanicalImageUrl, dim }) => {
  const photoSlot = getFramedPhotoSlot(coverPhotoOrientation);
  return (
  <View style={{
    position: 'absolute',
    left: dim.frontFullLeft,
    top: dim.frontFullTop,
    width: dim.panelW,
    height: dim.panelH,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  }}>
    {botanicalImageUrl && (
      <Image src={botanicalImageUrl} style={{
        position: 'absolute', top: 0, left: 0,
        width: '100%', height: '100%',
        objectFit: 'cover', objectPosition: 'center top',
      }} />
    )}

    {/* Frosted panel with photo — shifted dim.hingeCompensation (0.125" = half the hinge)
        toward the fore-edge so the panel centers on the optical midpoint of the visible
        flat board rather than the bare geometric trim midpoint. marginLeft pushes the panel rightward by
        exactly that amount; marginRight compensates so the panel stays at the correct width. */}
    <View style={{
      width: BOTANICAL_FRAMED.panelWidthPt,
      borderRadius: BOTANICAL_FRAMED.panelBorderRadius,
      borderWidth: 0.75,
      borderColor: BOTANICAL_FRAMED.panelBorderColor,
      padding: BOTANICAL_FRAMED.panelPadding,
      alignItems: 'center',
      position: 'relative',
      marginLeft: dim.hingeCompensation,
      marginRight: -dim.hingeCompensation,
    }}>
      <View style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: BOTANICAL_FRAMED.panelBg,
        opacity: BOTANICAL_FRAMED.panelOpacity,
        borderRadius: BOTANICAL_FRAMED.panelBorderRadius,
      }} />
      <Text style={{
        fontFamily: 'Lora', fontSize: BOTANICAL_FRAMED.titleFontPt, fontWeight: 500,
        color: BOTANICAL_FRAMED.titleColor, lineHeight: 1.2, marginBottom: 8, textAlign: 'center',
      }}>{title}</Text>
      <View style={{ width: BOTANICAL_FRAMED.dividerWidthPct, borderBottom: `0.8pt solid ${BOTANICAL_FRAMED.dividerColor}`, marginVertical: 5 }} />
      {coverPhotoUrl && (
        <View style={{
          width: photoSlot.widthPt, height: photoSlot.heightPt,
          border: `1pt solid ${BOTANICAL_FRAMED.photoBorderColor}`,
          padding: BOTANICAL_FRAMED.photoPadding, marginBottom: 8,
        }}>
          <Image src={coverPhotoUrl} style={{
            width: '100%', height: '100%', objectFit: 'cover',
            objectPosition: `center ${coverPhotoY}%`,
          }} />
        </View>
      )}
      <View style={{ width: BOTANICAL_FRAMED.dividerWidthPct, borderBottom: `0.8pt solid ${BOTANICAL_FRAMED.dividerColor}`, marginBottom: 8 }} />
      {subtitle ? <Text style={{
        fontFamily: 'Lora', fontStyle: 'italic', fontSize: BOTANICAL_FRAMED.subtitleFontPt,
        color: BOTANICAL_FRAMED.subtitleColor, lineHeight: 1.5, marginBottom: 4, textAlign: 'center',
      }}>{subtitle}</Text> : null}
      {author ? <Text style={{
        fontFamily: 'Lora', fontStyle: 'italic', fontSize: BOTANICAL_FRAMED.authorFontPt,
        color: BOTANICAL_FRAMED.authorColor, lineHeight: 1.6, marginBottom: 2, textAlign: 'center',
      }}>{author}</Text> : null}
      <Text style={{
        fontFamily: 'Lora', fontStyle: 'italic', fontSize: BOTANICAL_FRAMED.yearFontPt,
        color: BOTANICAL_FRAMED.yearColor,
      }}>{new Date().getFullYear()}</Text>
    </View>

  </View>
  );
};

// ═══════════════════════════════════════════════════════════════
// FRONT COVER — FLORAL WRAP (hand-illustrated wreath, no cream card)
// ═══════════════════════════════════════════════════════════════
//
// FLORAL-001 (see constants/coverLayout.js): content is placed directly in
// the wreath's clearing rather than behind a frosted cream panel (the old
// BOTANICAL_FRAMED card). FLORAL-002 later adds a small cream photo MAT —
// unrelated to that old full-panel card — with a teal keyline.
//
// COVER-002 (see docs/COVER_ARCHITECTURE.md): these components used to
// paint the front panel with a SEPARATE, independently-scaled crop
// (FLORAL_WRAP.frontPanelImageUrl) drawn on top of the page-level full-wrap
// stretch — two different scale factors sharing a boundary, which produced
// a visible seam at the trim edge. That overlay is REMOVED. The front panel
// now shows only the single page-level `fullWrapImageUrl` stretch rendered
// once by <RemnantCoverPDF> (seamless by construction — there is nothing
// left to misalign). Text/photo are centered on
// `mustardPlumOpeningFraction(dim)` from coverGeometry.js — the wreath
// opening's TRUE, script-derived center, projected through the same
// `objectFit: cover` transform the art itself goes through, recomputed per
// `dim` so registration holds at every spine width instead of only the
// reference spine (24-84pp).

// Small decorative flourish (line + diamond + dots), bundled as a PNG so the
// PDF and the HTML preview render an identical mark. Aspect is 12:1.
const FloralFlourish = ({ widthPt }) => (
  <Image src={MustardPlumFlourish} style={{ width: widthPt, height: widthPt / 12 }} />
);

/** Classic on floral wrap — title only, no photo.
 *  Design option D (distributed): when there is a subtitle or author, the
 *  title, a flourish, and the subtitle/author are spread down the clearing so
 *  it fills the vertical space. When the title is bare (no subtitle/author),
 *  it falls back to a centered flourish-framed arrangement so the lower half
 *  is not left empty. */
const FrontClassicFloralWrap = ({ title, subtitle, author, dim }) => {
  const L = FLORAL_WRAP_TITLEONLY;
  const hasMeta = !!(subtitle || author);
  const flourishW = floralFlourishWidthPt(title);
  const titleStyle = {
    fontFamily: 'Lora', fontWeight: 700, fontSize: floralTitleFontPt(title, false),
    color: FLORAL_WRAP.titleColor, textAlign: 'center', maxWidth: FLORAL_WRAP.clearingMaxWidthPt,
  };
  const subStyle = {
    fontFamily: 'Lora', fontStyle: 'italic', fontSize: FLORAL_WRAP.subtitlePt.classic,
    color: FLORAL_WRAP.subtitleColor, textAlign: 'center', lineHeight: 1.4, maxWidth: FLORAL_WRAP.clearingMaxWidthPt,
  };
  const authStyle = {
    fontFamily: 'Lora', fontStyle: 'italic', fontSize: FLORAL_WRAP.authorPt.classic,
    color: FLORAL_WRAP.subtitleColor, textAlign: 'center', maxWidth: FLORAL_WRAP.clearingMaxWidthPt,
  };
  // Derived opening center (COVER-002) — no local <Image> here. The front
  // panel's art is already painted by the single page-level fullWrapImageUrl
  // stretch in <RemnantCoverPDF>; this component only places content.
  const opening = mustardPlumOpeningFraction(dim);
  const openingXPt = opening.xFraction * dim.panelW;
  const contentBoxW = FLORAL_WRAP.clearingMaxWidthPt;
  const contentBoxLeft = openingXPt - contentBoxW / 2;
  const anchoredRow = (topFrac, node) => (
    <View style={{ position: 'absolute', left: contentBoxLeft, width: contentBoxW, top: topFrac * dim.panelH, alignItems: 'center' }}>{node}</View>
  );
  return (
    <View style={{ position: 'absolute', left: dim.frontFullLeft, top: dim.frontFullTop, width: dim.panelW, height: dim.panelH }}>
      {hasMeta ? (
        <>
          {anchoredRow(L.titleTop, <Text style={titleStyle}>{title}</Text>)}
          {anchoredRow(L.flourishTop, <FloralFlourish widthPt={flourishW} />)}
          {subtitle ? anchoredRow(L.subtitleTop, <Text style={subStyle}>{subtitle}</Text>) : null}
          {author ? anchoredRow(L.authorTop, <Text style={authStyle}>{author}</Text>) : null}
        </>
      ) : (
        // Bare title: flourish high, title centered, flourish low — spread down
        // the clearing (not a compact centered cluster) with larger ornaments.
        <>
          {anchoredRow(L.bareFlourishTop, <FloralFlourish widthPt={flourishW * L.bareFlourishScale} />)}
          {anchoredRow(L.bareTitleTop, <Text style={titleStyle}>{title}</Text>)}
          {anchoredRow(L.bareFlourishBottom, <FloralFlourish widthPt={flourishW * L.bareFlourishScale} />)}
        </>
      )}
    </View>
  );
};

/** Framed on floral wrap — title, photo (uniform white mat, centered exactly
    on the wreath opening), subtitle/author. coverPhotoUrl arrives already
    tone-treated upstream (color/sepia/bw baked in) — this component does not
    filter it, only places it.
 *
 * COVER-002 placement model: the PHOTO MAT's own center — not the centroid
 * of the whole title+photo+subtitle+author stack — is pinned exactly to the
 * derived opening point (mustardPlumOpeningFraction). Centering the whole
 * variable-height stack (title above, subtitle/author below don't sum to
 * equal heights) would leave the photo itself measurably off the true
 * center by however much that imbalance is — which is the one thing the
 * "framed" variant most needs to get right (deliverable: photo center
 * within ~1pt of the derived clearing center). Title is anchored just above
 * the mat's top edge; subtitle/author just below its bottom edge. When
 * there's no photo, there's no mat to anchor against, so title/subtitle/
 * author fall back to being centered as a block on the opening point. */
const FrontFramedFloralWrap = ({ title, subtitle, author, coverPhotoUrl, coverPhotoY = 50, coverPhotoOrientation = 'landscape', dim }) => {
  // Derived opening center (COVER-002) — no local <Image> here. The front
  // panel's art is already painted by the single page-level fullWrapImageUrl
  // stretch in <RemnantCoverPDF>; this component only places content,
  // centered on both axes of the true opening (not the panel's geometric
  // center — the opening sits measurably right of it, see coverGeometry.js).
  const opening = mustardPlumOpeningFraction(dim);
  const openingXPt = opening.xFraction * dim.panelW;
  const openingYPt = opening.yFraction * dim.panelH;
  const contentBoxW = FLORAL_WRAP.clearingMaxWidthPt;
  const contentBoxLeft = openingXPt - contentBoxW / 2;
  const frame = FLORAL_WRAP.photoFrames[coverPhotoOrientation] || FLORAL_WRAP.photoFrames.landscape;
  const mat = FLORAL_WRAP.matPt;
  const keylinePt = FLORAL_WRAP.matKeylinePt;
  const matOuterW = frame.widthPt + 2 * mat;
  const matOuterH = frame.heightPt + 2 * mat;
  const gapPt = 10; // matches the old marginBottom/marginTop spacing
  const titleStyle = {
    fontFamily: 'Lora', fontWeight: 700, fontSize: floralTitleFontPt(title, true),
    color: FLORAL_WRAP.titleColor, textAlign: 'center', maxWidth: FLORAL_WRAP.clearingMaxWidthPt,
  };
  const subStyle = {
    fontFamily: 'Lora', fontStyle: 'italic', fontSize: FLORAL_WRAP.subtitlePt.framed,
    color: FLORAL_WRAP.subtitleColor, textAlign: 'center', lineHeight: 1.4, maxWidth: FLORAL_WRAP.clearingMaxWidthPt,
  };
  const authStyle = {
    fontFamily: 'Lora', fontStyle: 'italic', fontSize: FLORAL_WRAP.authorPt.framed,
    color: FLORAL_WRAP.subtitleColor, textAlign: 'center', maxWidth: FLORAL_WRAP.clearingMaxWidthPt,
  };

  return (
  <View style={{
    position: 'absolute',
    left: dim.frontFullLeft,
    top: dim.frontFullTop,
    width: dim.panelW,
    height: dim.panelH,
  }}>
    {coverPhotoUrl ? (
      <>
        {/* Photo mat — center pinned exactly to the derived opening point. */}
        <View style={{
          position: 'absolute',
          left: openingXPt - matOuterW / 2,
          top: openingYPt - matOuterH / 2,
          backgroundColor: FLORAL_WRAP.matCreamColor, padding: mat - keylinePt,
        }}>
          {/* FLORAL-002: cream mat with a thin teal keyline hugging the photo,
              nested INSIDE the same total mat footprint used in FLORAL-001
              (photo -> matKeylinePt teal -> (matPt - matKeylinePt) cream ->
              outer edge). `padding` is a single scalar applied equally to all
              four sides on both the outer (cream) and inner (teal) View, so
              both rings stay uniform (L = R = T = B) by construction — no
              drop shadow, the two-tone mat is the only edge treatment. */}
          <View style={{ backgroundColor: FLORAL_WRAP.titleColor, padding: keylinePt }}>
            <Image src={coverPhotoUrl} style={{
              width: frame.widthPt, height: frame.heightPt, objectFit: 'cover',
              objectPosition: `center ${coverPhotoY}%`,
            }} />
          </View>
        </View>

        {/* Title — bottom-anchored just above the mat's top edge. */}
        <View style={{ position: 'absolute', left: contentBoxLeft, width: contentBoxW, bottom: dim.panelH - (openingYPt - matOuterH / 2 - gapPt), alignItems: 'center' }}>
          <Text style={titleStyle}>{title}</Text>
        </View>

        {/* Subtitle/author — top-anchored just below the mat's bottom edge. */}
        {(subtitle || author) && (
          <View style={{ position: 'absolute', left: contentBoxLeft, width: contentBoxW, top: openingYPt + matOuterH / 2 + gapPt, alignItems: 'center' }}>
            {subtitle ? <Text style={{ ...subStyle, marginBottom: 3 }}>{subtitle}</Text> : null}
            {author ? <Text style={{ ...authStyle, marginTop: subtitle ? 2 : 0 }}>{author}</Text> : null}
          </View>
        )}
      </>
    ) : (
      // No photo: no mat to anchor against — center the text block itself
      // on the opening point, as a flex column (its own centroid, not any
      // single line, lands on the derived center).
      <View style={{
        position: 'absolute', left: contentBoxLeft, width: contentBoxW,
        top: openingYPt - dim.panelH / 2, height: dim.panelH,
        justifyContent: 'center', alignItems: 'center', flexDirection: 'column',
      }}>
        <Text style={{ ...titleStyle, marginBottom: 10 }}>{title}</Text>
        {subtitle ? <Text style={{ ...subStyle, marginBottom: 3 }}>{subtitle}</Text> : null}
        {author ? <Text style={{ ...authStyle, marginTop: 2 }}>{author}</Text> : null}
      </View>
    )}
  </View>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COVER COMPONENT
// ═══════════════════════════════════════════════════════════════

const coverComponents = {
  'classic-charcoal': FrontClassicCharcoal,
  'classic-burgundy': FrontClassicBotanical,
  'classic-mothersday': FrontClassicBotanical,
  'classic-linen': FrontClassicBotanical,
  'classic-charcoalgold': FrontClassicBotanical,
  'classic-floralwrap': FrontClassicBotanical,
  'classic-burgwrap': FrontClassicBotanical,
  'classic-mustardplum': FrontClassicFloralWrap,
  // F-308: a seamless all-over repeat, so it takes the BOTANICAL path (like burgwrap), not the
  // wreath-axis FloralWrap path. Nothing on this art has to line up with the spine.
  'classic-slatechevron': FrontClassicBotanical,
  'framed-charcoal': FrontFramedCharcoal,
  'framed-burgundy': FrontFramedBotanical,
  'framed-mothersday': FrontFramedBotanical,
  'framed-linen': FrontFramedBotanical,
  'framed-charcoalgold': FrontFramedBotanical,
  'framed-floralwrap': FrontFramedBotanical,
  'framed-burgwrap': FrontFramedBotanical,
  'framed-mustardplum': FrontFramedFloralWrap,
  'framed-slatechevron': FrontFramedBotanical,
};

const RemnantCoverPDF = ({
  title = 'Our Story',
  subtitle = '',
  author = '',
  pageCount = 24,
  coverStyle = 'classic',
  coverDesign = 'charcoal',
  coverPhotoUrl = null,
  coverPhotoY = 50,
  coverPhotoOrientation = 'landscape',
  botanicalImageUrl = null,
  fullWrapImageUrl = null,
  debug = false,
}) => {
  const dim = getCoverDimensions(pageCount);

  // Legacy coverStyle values map to the new model
  const normalizedStyle = ['classic', 'framed'].includes(coverStyle) ? coverStyle : 'classic';
  const normalizedDesign = ['charcoal', 'burgundy', 'mothersday', 'linen', 'charcoalgold', 'floralwrap', 'burgwrap', 'mustardplum', 'slatechevron'].includes(coverDesign) ? coverDesign : 'charcoal';
  const isBotanical = normalizedDesign !== 'charcoal';
  const isFullWrap = !!fullWrapImageUrl;
  // F-COVER-CROP: Mustard Plum places the wrap art via the shared, spine-aware
  // wreath-axis-centered transform (coverGeometry.fullWrapPlacement) — the SAME
  // one the Book Builder preview uses (COVER-001). floralwrap/burgwrap keep the
  // plain edge-to-edge objectFit:cover (unchanged, out of scope).
  const mustardPlacement = (isFullWrap && normalizedDesign === 'mustardplum') ? fullWrapPlacement(dim) : null;

  const coverKey = `${normalizedStyle}-${normalizedDesign}`;
  const FrontCover = coverComponents[coverKey] || FrontClassicCharcoal;
  const bgColor = isBotanical ? '#f5ede4' : '#2c2420';

  return (
    <Document
      title={`${title} — Cover`}
      creator="Talebound — taleboundbook.com"
    >
      <Page size={[dim.widthPt, dim.heightPt]} style={{
        width: dim.widthPt,
        height: dim.heightPt,
        position: 'relative',
        backgroundColor: bgColor,
      }}>
        {/* Full wrap image — spans entire cover sheet (back + spine + front).
            Mustard Plum uses an explicit wreath-axis-centered placement (F-COVER-CROP):
            the placed image is intentionally LARGER than the sheet, so it is wrapped in
            a page-sized overflow:hidden View (the layout box stays page-sized — an
            oversized Image in the page flow trips react-pdf's pagination) and clipped to
            the sheet, exactly like the preview's clipping div. Other full-wrap designs
            use edge-to-edge objectFit:cover. */}
        {isFullWrap && (mustardPlacement ? (
          <View style={{ position: 'absolute', top: 0, left: 0, width: dim.widthPt, height: dim.heightPt, overflow: 'hidden' }}>
            <Image src={fullWrapImageUrl} style={{
              position: 'absolute',
              left: mustardPlacement.leftPt,
              top: mustardPlacement.topPt,
              width: mustardPlacement.widthPt,
              height: mustardPlacement.heightPt,
            }} />
          </View>
        ) : (
          <Image src={fullWrapImageUrl} style={{
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%',
            objectFit: 'cover',
          }} />
        ))}
        <BackAndSpine title={title} dim={dim} coverDesign={normalizedDesign} botanicalImageUrl={isFullWrap ? null : botanicalImageUrl} />
        <FrontCover
          title={title}
          subtitle={subtitle}
          author={author}
          coverPhotoUrl={coverPhotoUrl}
          coverPhotoY={coverPhotoY}
          coverPhotoOrientation={coverPhotoOrientation}
          botanicalImageUrl={isFullWrap ? null : botanicalImageUrl}
          dim={dim}
        />
        {debug && <DebugGuides dim={dim} />}
      </Page>
    </Document>
  );
};

export default RemnantCoverPDF;